import { css, html, LitElement, PropertyValues} from 'lit';
import {customElement, property, queryAsync, state} from 'lit/decorators.js';
import {SpAnimationFactory, SpAnimationOptions} from "../../styles/animations/animation.interfaces";
import {classMap} from "lit/directives/class-map.js";
import {SpValidationService} from "../../services/sp-validation.service";
import {when} from "lit/directives/when.js";
import {styleMap} from "lit-html/directives/style-map.js";

@customElement('sp-drop-zone')
export class SpDropZone extends LitElement {
    // Ignore events where the dataTransfer items contain at least one of the allowed mime types. Wildcards (e.g. image/*) can be used
    @property({type: Array})
    forbiddenDataTypes?: string[];
    // Allow events where the dataTransfer items contain at least one of the allowed mime types. Wildcards (e.g. image/*) can be used
    @property({type: Array})
    mandatoryDataTypes?: string[];
    @property({type: String})
    icon: string;
    @property({type: Array})
    animations: SpDropZoneAnimation<any>[] = [];
    // Set to true if you want the dropzone to be functional but not visible
    @property({type: Boolean})
    invisible = false;

    @state()
    state: SpDropZoneState = 'default';

    dragStartListener = this.dragStart.bind(this);
    dragEnterListener = this.dragEnter.bind(this);
    dragEndListener = this.dragEnd.bind(this);
    dragEnterDropZoneListener = this.dragEnterDropZone.bind(this);
    dragOverDropZoneListener = this.dragOverDropZone.bind(this);
    dragLeaveDropZoneListener = this.dragLeaveDropZone.bind(this);
    dropListener = this.drop.bind(this);

    listening = false;
    validatorsPassed: boolean = null;

    static get styles() {
        return [
            //language=css
            css`
               
                .container {
                    display: none;
                    position: absolute;
                    justify-content: center;
                    width: calc(100% - 2 * var(--sp-drop-zone-border-width));
                    height: calc(100% - 2 * var(--sp-drop-zone-border-width));
                    z-index: 9999;
                    border-radius: var(--sp-default-border-radius, 0.18rem);
                }
                
                .dragging {
                    display: flex;
                    background-color: var(--sp-drop-zone-background-color);
                    border: var(--sp-drop-zone-border-color) var(--sp-drop-zone-border-width) dashed;
                }
                
                .drag-over {
                    display: flex;
                    background-color: var(--sp-drop-zone-background-color-active);
                    border: var(--sp-drop-zone-border-color-active) var(--sp-drop-zone-border-width) dashed;
                }
                
                .icon {
                    align-self: center;
                    background-repeat: no-repeat;
                    background-size: cover;
                    background-position: center;
                    height: 70%;
                    aspect-ratio: 1 / 1;
                    pointer-events: none;
                }
                
                .invisible {
                    opacity: 0;
                }
            `
        ];
    }

    @queryAsync('#container') containerElem: Promise<HTMLElement>;

    protected willUpdate(_changedProperties: PropertyValues) {
        super.willUpdate(_changedProperties);
        if (_changedProperties.has('animations') && Array.isArray(this.animations)) {
            this.shadowRoot.adoptedStyleSheets = [
                ...this.animations.map((animation, index) => animation.animation({
                    ...animation.animationOptions,
                    className: `animation-${index}`
                }).styleSheet),
                ...this.shadowRoot.adoptedStyleSheets ];
        }
    }

    connectedCallback() {
        super.connectedCallback();
        this.startListening();
    }

    disconnectedCallback() {
        super.disconnectedCallback();
        this.stopListening();
    }

    render() {
        return html`
            <div part="container"
                 id="container"
                 class=${ classMap({
                     invisible: this.invisible,
                     container: true,
                     dragging: this.state === 'dragging',
                     'drag-over': this.state === 'dragOver',
                     ...!this.invisible && { ...this.getAnimationClassMap('container') }
                }) }>
                ${ when(this.icon, () => html`<div part="icon"
                   class=${classMap({
                        icon: true,
                        ...this.getAnimationClassMap('icon')
                    })}
                    style=${ styleMap({
                        'background-image': `url(${ this.icon })`
                    }) }></div>`) }
            </div>
        `;
    }

    private getAnimationClassMap(part: SpDropZonePart): { [key: string]: any } {
        let animations = {};
        this.animations
            .forEach((animation, number) => {
                if (animation.animationTargetPart === part) {
                    animations[`animation-${number}`] = animation.animationTargetStates.includes(this.state)
                }
            });
        return animations;
    }

    private async startListening() {
        if (!this.listening) {
            window.addEventListener('blur', this.dragEndListener);
            document.addEventListener('dragenter', this.dragEnterListener);
            document.addEventListener('dragstart', this.dragStartListener);
            document.addEventListener('dragleave', e => {
                // Webkit fires dragleave on every element that is left, that's why we need that extra check (chromium only fires when "document" is left):
                // TODO: only apply in webkit?
                if (e.clientX <= 0 || e.clientX >= window.innerWidth || e.clientY <= 0 || e.clientY >= window.innerHeight) {
                    this.dragEndListener();
                }
            });
            document.addEventListener('dragend', this.dragEndListener);
            document.addEventListener('drop', this.dragEndListener);
            (await this.containerElem).addEventListener('dragenter', this.dragEnterDropZoneListener);
            (await this.containerElem).addEventListener('dragover', this.dragOverDropZoneListener);
            (await this.containerElem).addEventListener('dragleave', this.dragLeaveDropZoneListener);
            (await this.containerElem).addEventListener('drop', this.dropListener);
            this.listening = true;
        }
    }

    private async stopListening() {
        if (this.listening) {
            window.removeEventListener('blur', this.dragEndListener);
            document.removeEventListener('dragenter', this.dragEnterListener);
            document.removeEventListener('dragstart', this.dragStartListener);
            document.removeEventListener('dragleave', this.dragEndListener);
            document.removeEventListener('dragend', this.dragEndListener);
            document.removeEventListener('drop', this.dragEndListener);
            (await this.containerElem).removeEventListener('dragenter', this.dragEnterDropZoneListener);
            (await this.containerElem).removeEventListener('dragover', this.dragOverDropZoneListener);
            (await this.containerElem).removeEventListener('dragleave', this.dragLeaveDropZoneListener);
            (await this.containerElem).removeEventListener('drop', this.dropListener);
            this.listening = false;
        }
    }

    private passesForbiddenDataTypesFilter(e: DragEvent): boolean {
        const transferItems = Array.from(e.dataTransfer.items);
        const found = transferItems?.find(item => SpValidationService.validateMimeTypes(item.type, this.forbiddenDataTypes));
        return !(!!found);
    }

    private passesMandatoryDataTypesFilter(e: DragEvent): boolean {
        const transferItems = Array.from(e.dataTransfer.items);
        const found = transferItems?.find(item => SpValidationService.validateMimeTypes(item.type, this.mandatoryDataTypes));
        return !!found;
    }

    private passesFilter(e: DragEvent) {
        return (!this.mandatoryDataTypes || this.passesMandatoryDataTypesFilter(e)) && (!this.forbiddenDataTypes || this.passesForbiddenDataTypesFilter(e));
    }

    private dragStart(e: DragEvent) {
        this.validatorsPassed = this.passesFilter(e);
    }

    private async dragEnter(e: DragEvent) {
        if (!!this.validatorsPassed
            // This is only checked when dragstart didn't fire
            || (this.validatorsPassed === null && this.passesFilter(e))) {
            this.setState('dragging');
        }
        this.validatorsPassed = null;
    }

    private dragEnd(e: DragEvent) {
        this.setState('default');
    }

    private async dragEnterDropZone(e: DragEvent) {
        // Necessary to enable drop event for this element: https://developer.mozilla.org/en-US/docs/Web/API/HTML_Drag_and_Drop_API/Drag_operations#specifying_drop_targets
        e.preventDefault();
        this.setState('dragOver');
    }

    private async dragOverDropZone(e: DragEvent) {
        // Necessary to enable drop event for this element: https://developer.mozilla.org/en-US/docs/Web/API/HTML_Drag_and_Drop_API/Drag_operations#specifying_drop_targets
        e.preventDefault();
    }

    private async dragLeaveDropZone(e: DragEvent) {
        this.setState('dragging');
    }

    private async drop(e: DragEvent) {
        this.setState('default');
        e.preventDefault() // Prevent the default drop behaviour
    }

    private setState(state: SpDropZoneState) {
        this.state = state;
    }
}

export type SpDropZonePart = 'container' | 'icon';
export const spDropZoneState = ['default', 'dragging', 'dragOver'] as const;
export type SpDropZoneState = typeof spDropZoneState[number];
export type SpDropZoneAnimation<AnimationOptions extends SpAnimationOptions> = {
    animation: SpAnimationFactory<AnimationOptions>,
    animationOptions?: Omit<AnimationOptions, 'className'>,
    animationTargetPart: SpDropZonePart,
    animationTargetStates: SpDropZoneState[]
}